import { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import InteractiveMap from './InteractiveMap';

export default function Enquire() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', unit: '', message: '',
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Please fill in required fields', description: 'Name and email are required.' });
      return;
    }
    toast({
      title: 'Enquiry sent',
      description: 'Thanks — we\u2019ll be in touch shortly about Valley Mills.',
    });
    setForm({ name: '', company: '', email: '', phone: '', unit: '', message: '' });
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="enquire" className="bg-[#f1ede5] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">Enquire Today</span>
          <h2 className="font-serif-display text-[42px] md:text-[54px] leading-[1.05] mt-6">
            Find your next<br />
            <em className="text-[#b39352] italic">business home.</em>
          </h2>
          <p className="mt-6 text-[14px] leading-[1.85] text-[#0d281e]/80 max-w-xl">
            Speak to the letting team about availability, viewings and bespoke fit-outs.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full border border-[#b39352] flex items-center justify-center shrink-0">
                  <Phone size={16} className="text-[#b39352]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#0d281e]/60">Telephone</div>
                  <a href="tel:01138474474" className="font-serif-display text-[26px] text-[#0d281e] hover:text-[#b39352] transition-colors">
                    0113 847 4474
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full border border-[#b39352] flex items-center justify-center shrink-0">
                  <Mail size={16} className="text-[#b39352]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#0d281e]/60">Email</div>
                  <a href="mailto:sales@valleymills.co.uk" className="font-serif-display text-[26px] text-[#0d281e] hover:text-[#b39352] transition-colors">
                    sales@valleymills.co.uk
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full border border-[#b39352] flex items-center justify-center shrink-0">
                  <MapPin size={16} className="text-[#b39352]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#0d281e]/60">Address</div>
                  <div className="font-serif-display text-[22px] text-[#0d281e] leading-tight mt-1">
                    151 Kent Road<br />Pudsey, Leeds<br />LS28 9NF
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <InteractiveMap />
            </div>
          </div>

          <form onSubmit={submit} className="bg-white border border-[#0d281e]/10 p-8 md:p-10 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Name*" value={form.name} onChange={upd('name')} />
              <Field label="Company" value={form.company} onChange={upd('company')} />
              <Field label="Email*" value={form.email} onChange={upd('email')} type="email" />
              <Field label="Phone" value={form.phone} onChange={upd('phone')} />
              <div className="md:col-span-2">
                <Field label="Unit of Interest" value={form.unit} onChange={upd('unit')} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#0d281e]/60 block mb-3">Message</label>
                <textarea
                  value={form.message}
                  onChange={upd('message')}
                  rows={4}
                  placeholder="Tell us about your business and requirements..."
                  className="w-full bg-transparent border-b border-[#0d281e]/20 focus:border-[#b39352] outline-none text-[14px] py-2 resize-none placeholder:text-[#0d281e]/40 transition-colors"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="text-[11px] text-[#0d281e]/60 max-w-xs leading-relaxed">
                We\u2019ll use your details solely to respond to your enquiry about Valley Mills.
              </p>
              <button type="submit" className="btn-gold">Send Enquiry</button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-[#0d281e]/60 block mb-3">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-[#0d281e]/20 focus:border-[#b39352] outline-none text-[14px] py-2 transition-colors"
      />
    </div>
  );
}
